import { useEffect, useState } from 'react';
import { mutate } from 'swr';

import { decodeUser, Account, LoggedUser } from '../session/session-decode';
import { isIntegrator } from '../session/user-type';
import { buildPersonName } from './core/pessoa-nome';
import { resolveDisplayPhoto, photoForCache } from './core/photo';
import { buildAccountList, excludeAccess } from './core/accounts';
import { Locale } from './core/locale';
import { decodeAuthorities } from '../session/permissions';
import {
  shouldShowSettings,
  shouldShowMyData,
  shouldShowAccountName,
  shouldShowPerson,
  shouldShowAccountSelector,
  shouldShowIntegratorAccountSelector,
  shouldShowChangePassword,
} from './core/visibility';
import {
  getTenantName,
  getTenantStorage,
  clearTenantStorage,
  saveTenantName,
  saveTenantStorage,
} from '../session/tenant-storage';
import { beginAccountSwitch, endAccountSwitchOnTokenRefresh } from '../session/account-switch';
import { identifyFromSession } from '../session/session-identity';
import { getPhotoCache, savePhotoCache } from './effects/foto-cache';
import { changeLocale, useIntegratorAccounts, useLocales, useLoggedPerson } from './effects/data';
import { delegateRefreshToken } from './effects/auth-delegation';
import { performLogout } from './effects/logout';
import { navigateToRoute } from './effects/navigate';
import { applyLanguage } from './effects/language';
import { getAccessToken } from '../../http/auth-storage';

// Não há um efeito dedicado para user_instance (só session-decode + a leitura crua
// aqui): é a única chave do contrato sem um módulo de storage próprio.
const USER_INSTANCE_KEY = 'user_instance';

const LOGGED_PERSON_KEY = 'pessoas/pessoa_logada';

// Só as duas severidades que a navbar legada exibe: até a falha ao salvar o idioma é aviso.
export type NotificationSeverity = 'success' | 'warning';

export interface NotificationState {
  /** Chave i18n, traduzida no render pelo `NavbarNotification`. */
  messageKey: string;
  severity: NotificationSeverity;
}

export interface NavbarViewModel {
  personName: string | null;
  personPhoto: string | null;
  accountName: string | null;
  showAccountSelector: boolean;
  accounts: Account[];
  filter: string;
  setFilter: (v: string) => void;
  showMyData: boolean;
  showSettings: boolean;
  showSelectAccount: boolean;
  showChangePassword: boolean;
  locales: Locale[];
  notification: NotificationState | null;
  onSelectAccount: (account: Account) => void;
  onLeaveAccount: () => void;
  onLogout: () => void;
  onNavigate: (path: string) => void;
  onChangeLanguage: (locale: Locale) => void;
  onDismissNotification: () => void;
}

function readLoggedUser(): LoggedUser | null {
  try {
    return decodeUser(sessionStorage.getItem(USER_INSTANCE_KEY));
  } catch {
    return null;
  }
}

/** Costura storage + núcleo puro + efeitos + skin em um view-model para a Navbar. */
export function useNavbar(): NavbarViewModel {
  const [filter, setFilter] = useState('');
  // tenant/nome_conta são gravados fora do React (storage); esse contador força o
  // hook a reler o storage após onSelectAccount/onLeaveAccount.
  const [, forceRefresh] = useState(0);
  // Notificação como estado observável pela UI (em vez de log): o componente
  // renderiza o NavbarNotification a partir daqui e chama onDismissNotification para limpar.
  const [notification, setNotification] = useState<NotificationState | null>(null);

  function notify(messageKey: string, severity: NotificationSeverity): void {
    setNotification({ messageKey, severity });
  }

  function onDismissNotification(): void {
    setNotification(null);
  }

  const user = readLoggedUser();
  const tenant = getTenantStorage();
  const storedAccountName = getTenantName();
  const photoCache = getPhotoCache();
  const token = getAccessToken();
  const authorities = decodeAuthorities(token);
  const route = window.location.hash;

  const personEnabled = shouldShowPerson(user, !!tenant);
  const integratorAccountsEnabled = isIntegrator(user);

  const { person } = useLoggedPerson(personEnabled);
  const { accounts: integratorAccounts } = useIntegratorAccounts(integratorAccountsEnabled);
  const { locales } = useLocales();

  useEffect(() => {
    if (!person) {
      return;
    }
    // Só persiste quando a pessoa chegou pelo endpoint (o cache já cobre o caso contrário).
    savePhotoCache(photoForCache(person));
  }, [person]);

  const personName = buildPersonName(person);
  const personPhoto = resolveDisplayPhoto(photoCache, person);
  const accounts = buildAccountList(user, integratorAccounts, filter);
  const accountName = shouldShowAccountName(route, storedAccountName) ? storedAccountName : null;
  const showMyData = shouldShowMyData(user);
  const showSettings = shouldShowSettings(user, authorities);
  const showChangePassword = shouldShowChangePassword(user, !!tenant);

  // Divergência deliberada de `showAccountSelector`: aqui é só o predicado puro
  // (sem o ramo de integrador), por paridade com o item de menu do Angular legado.
  const showSelectAccount = shouldShowAccountSelector(user);

  const showAccountSelector = isIntegrator(user)
    ? shouldShowIntegratorAccountSelector(excludeAccess(integratorAccounts ?? []))
    : shouldShowAccountSelector(user);

  function refresh(): void {
    forceRefresh((tick) => tick + 1);
  }

  function onSelectAccount(account: Account): void {
    if (account.acessoBloqueado) {
      notify('navbar.notification.accountBlocked', 'warning');
      return;
    }

    const previousToken = getAccessToken();
    // Sinaliza a transição antes de mexer no storage, para a sidebar já entrar em
    // carregamento e não renderizar o menu com tenant novo + token antigo.
    beginAccountSwitch();
    saveTenantStorage(account);
    saveTenantName(account.nomeConta);
    delegateRefreshToken(account);
    // O refresh do token (com as permissões da conta) é assíncrono; encerra a
    // transição e re-notifica a sidebar quando ele chega. A reidentificação roda só
    // nesse ponto, com o tenant já corrente no storage.
    endAccountSwitchOnTokenRefresh(previousToken, identifyFromSession);
    refresh();
    // O tenant muda fora do estado do React; o SWR não revalida sozinho
    // (swrConfig é conservador), então força a rebusca da pessoa da nova conta.
    void mutate(LOGGED_PERSON_KEY);
  }

  function onLeaveAccount(): void {
    const previousToken = getAccessToken();
    beginAccountSwitch();
    clearTenantStorage();
    saveTenantName(null);
    delegateRefreshToken(null);
    // O token volta ao escopo de integrador de forma assíncrona; encerra a
    // transição e re-notifica a sidebar quando ele chega. Ao sair da conta, a
    // identificação perde o tenant_id do contexto anterior.
    endAccountSwitchOnTokenRefresh(previousToken, identifyFromSession);
    refresh();
  }

  function onLogout(): void {
    // Fire-and-forget: a UI não espera o teardown; a navegação para o login
    // acontece dentro de performLogout.
    void performLogout();
  }

  function onNavigate(path: string): void {
    navigateToRoute(path);
  }

  function onChangeLanguage(locale: Locale): void {
    void applyLanguage(locale);
    changeLocale(locale)
      .then(() => notify('navbar.notification.languageChanged', 'success'))
      .catch(() => notify('navbar.notification.languageChangeFailed', 'warning'));
  }

  return {
    personName,
    personPhoto,
    accountName,
    showAccountSelector,
    accounts,
    filter,
    setFilter,
    showMyData,
    showSettings,
    showSelectAccount,
    showChangePassword,
    locales,
    notification,
    onSelectAccount,
    onLeaveAccount,
    onLogout,
    onNavigate,
    onChangeLanguage,
    onDismissNotification,
  };
}
